import Icon from "@ant-design/icons";
/* eslint-disable consistent-return */
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Layout,
  Row,
  Spin,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";

import { getSupaClient } from "../../lib/supabase";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { createDiscordUrl } from "../../utils/constants";
import invariant from "ts-invariant";
import DotsFishLoading from "../DotsFishLoading/DotsFishLoading";

const { Content } = Layout;

const MainPlatformButton = ({
  chosenPlatform,
  icon,
  pathTo,
}: {
  chosenPlatform: string;
  icon: string;
  pathTo: string;
}) => {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "18px" }}>
      <a href={pathTo}>
        <Button
          size="large"
          type="default"
          style={{
            lineHeight: "normal",
            fontWeight: "bold",
            borderWidth: "2px",
            paddingLeft: "30px",
            paddingRight: "30px",
            height: "55px",
          }}
          icon={
            <Icon
              component={() => <img src={icon} alt="discord" width="18" />}
            />
          }
          onClick={() => {
            window.analytics.track(
              `Sign up - ${chosenPlatform} platform selected`
            );
          }}
        >
          {chosenPlatform}
        </Button>
      </a>
    </div>
  );
};

/**
 *
 * Present the UI for the user to sign in
 *
 * @returns
 */
const NotLoggedIn = () => {
  return (
    <div>
      <Content style={{ textAlign: "center" }}>
        <Row style={{ marginTop: "100px" }}>
          <Col span={8} offset={8}>
            <Alert
              message="No session"
              description="Please sign in to continue the onboarding. Contact us for support at hello@dots.community!"
              type="error"
            />
          </Col>
        </Row>
        <Row style={{ marginTop: "10px" }}>
          <Col span={8} offset={8}>
            <a href="/signin">
              <Button type="link">Back to Sign In </Button>
            </a>
          </Col>
        </Row>
      </Content>
    </div>
  );
};

/**
 *
 * 1. Check if the invite code is valid. If invalid, return false
 * 2. If the code is valid, mark it as used in the DB, and set the user's onboarding status to "choose-platform", and return true
 *
 * NOTE: initially we just use a hard-coded invite code for simplicity.
 *
 * @param user
 * @param code
 * @returns
 */
async function validateAndConsumeInviteCode(user: User, code: string) {
  // TODO 1. real code that checks the invite code.
  // 2. if valid, change the user's status to `choose-platform`
  const lowerCode = code.toLowerCase();
  await new Promise((resolve) => setTimeout(resolve, 1000));
  const valid =
    lowerCode === (process.env["REACT_APP_INVITE_CODE"] ?? "7f03d5f4") ||
    lowerCode === (process.env["REACT_APP_INVITE_CODE"] ?? "launchhn") ||
    lowerCode === (process.env["REACT_APP_INVITE_CODE"] ?? "phlaunch");
  if (valid) {
    invariant(user?.email);

    setOnboardingStatus(user.email, "choose-platform");
    window.analytics.track("Sign up - past invite code");
  }
  return valid;
}

/**
 * Returns a [React Query mutation object](https://react-query-v3.tanstack.com/guides/mutations) that consumes the specified invite code.
 *
 * Usage:
 *
 * ```ts
 * function Comp() {
 *   const mutation = useConsumeInviteCodeMutation(user)
 *   ...
 *   const handleSubmit = () => {
 *     mutation.mutate(inviteCode);
 *   }
 * }
 * ```
 *
 * When `mutation.mutate()` is called, we invoke `validateAndConsumeInviteCode`, which "consumes" the invite code. i.e.:
 * 1. Check if the invite code is valid. If invalid, throw an error
 * 2. If the code is valid, mark it as used in the DB, and set the user's onboarding status to "choose-platform".
 *
 *
 * @param user
 * @returns
 */
function useConsumeInviteCodeMutation(user: User | null) {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, string | undefined>(
    async (code?: string) => {
      if (!code) throw new Error("Empty invite code!");
      if (user) {
        const valid = await validateAndConsumeInviteCode(user, code);
        if (valid) {
          // Update `onboarding-status` in React Query cache
          queryClient.setQueryData(
            ["onboarding-status", { email: user?.email }],
            "choose-platform"
          );
          return valid;
        } else {
          throw new Error("Invalid invite code!");
        }
      } else {
        throw new Error("User not logged in!");
      }
    }
  );
}

type OnboardingStatus =
  // Legend: triggering event ;; UI content at this state
  | "queued" // signed up but hasn't received invite code ;; "We'll send you invite code..."
  | "invited" // received invite code ;; "Enter invite code"
  | "choose-platform" // entered invite code ;; "Choose platform"
  | "slack-await-approval" // Team member picked ;; "We will need your admin to confirm..."
  | "discord-pick-guilds" // discord authorized ;; "List of servers"
  | "discord-bot-added" // bot added ;; "Two more quick things.."
  | "discord-all-set" // "All set" clicked ;; "You are live..."
  | "onboarded"; // discord: Edit flow clicked; slack: admin picked & authorized ;; redirect: "/home"

function createOnboardingKey(
  userEmail: string,
  type: "status" | "in-progress"
) {
  return `${userEmail}-onboarding-${type}`;
}

/**
 *
 * Set onboarding entries in local storage. There are two entries:
 * 1. {{userEmail}}-onboarding-status
 * 2. {{userEmail}}-onboarding-in-progress
 *
 * These two entries work together to make sure that:
 * - We should redirect to /onboarding after the user signs in if onboarding is in progress.
 * - We should NOT do the above redirect for existing users
 * - We should NOT do the above redirect if the user adds a new community via Settings
 *
 * @param userEmail
 * @param status
 */
export function setOnboardingStatus(
  userEmail: string,
  status: OnboardingStatus
) {
  localStorage.setItem(createOnboardingKey(userEmail, "status"), status);
  switch (status) {
    case "choose-platform":
      localStorage.setItem(
        createOnboardingKey(userEmail, "in-progress"),
        "true"
      );
      break;
    case "onboarded":
      localStorage.removeItem(createOnboardingKey(userEmail, "in-progress"));
      break;
  }
}

/**
 *
 * @param userEmail
 * @returns The onboarding status based on userEmail. If the in-progress entry is missing, we consider the user is a previously onboarded user.
 */
function getOnboardingStatus(userEmail: string) {
  // if onboarding status isn't found, we consider the user pre-exisiting.
  const inProgress = Boolean(
    localStorage.getItem(createOnboardingKey(userEmail, "in-progress"))
  );
  return (
    inProgress
      ? localStorage.getItem(createOnboardingKey(userEmail, "status")) ??
        "onboarded"
      : "onboarded"
  ) as OnboardingStatus;
}

/**
 * Get the onboarding status of the specified user. We use React Query
 * to cache the result.
 *
 * @param user
 * @returns A React Query result, an object with shape: {data, isLoading, error, ...}
 */
export function useOnboardingStatus(user: User | null) {
  const result = useQuery<OnboardingStatus, Error>(
    ["onboarding-status", { email: user?.email }],
    async () => {
      if (user?.email) {
        return getOnboardingStatus(user.email);
      } else {
        throw new Error("Not logged in");
      }
    }
  );
  return result;
}

/**
 *
 * Present the UI for the user to choose from supported platforms, e.g. Discord and Slack
 *
 * @returns
 */
const ChoosePlatform = () => {
  return (
    <Row
      justify="center"
      align="middle"
      style={{
        flexDirection: "column",
        minHeight: "100vh",
        padding: "0 200px",
      }}
    >
      <Card
        style={{
          textAlign: "center",
          width: "75%",
          height: "580px",
        }}
      >
        <Row
          align="middle"
          justify="center"
          style={{
            margin: "1rem 0 4rem 0",
          }}
        >
          <h2 style={{ fontWeight: "bold", color: "black" }}>👋🏽 Welcome!</h2>
        </Row>
        <Row
          align="middle"
          justify="center"
          style={{
            margin: "0 0 1rem 0",
            fontWeight: "500",
            fontSize: "16px",
          }}
        >
          What is your main community platform?
        </Row>
        <Row align="middle" justify="center">
          <MainPlatformButton
            chosenPlatform="Discord"
            icon="/discord.svg"
            pathTo={createDiscordUrl("oauth")}
          />
          <MainPlatformButton
            chosenPlatform="Slack"
            icon="/slack_main.svg"
            pathTo="/onboarding/slack"
          />
        </Row>
      </Card>
      <div
        style={{
          background: "#FFFFFF",
          fontSize: 12,
          bottom: 0,

          textAlign: "center",
          justifyContent: "space-evenly",
          fontWeight: "500",
          position: "absolute",
          marginBottom: "1.7rem",
        }}
      >
        Use chat, <a href="https://discord.gg/WJFTPtvGGw"> discord</a>, or
        <a href="mailto:hello@dots.community"> email </a> for help!
      </div>
    </Row>
  );
};

const Queued = () => (
  <div>You are in the queue. We'll send you the invite code.</div>
);

/**
 *
 * Present the UI that asks the user to enter an invite code and validates it
 *
 * @returns
 */
const CheckInviteCode = () => {
  const user = getSupaClient().auth.user();
  const {
    mutate: consumeInviteCode,
    isLoading,
    error,
  } = useConsumeInviteCodeMutation(user);
  const [inviteCode, setInviteCode] = useState<string>();
  return (
    <Row
      justify="center"
      align="middle"
      style={{
        flexDirection: "column",
        minHeight: "100vh",
        padding: "0 200px",
      }}
    >
      <Card className="w-80">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            consumeInviteCode(inviteCode);
          }}
          className="flex flex-col gap-4"
        >
          <label className="flex flex-col gap-4">
            <Typography.Text>Enter your invite code:</Typography.Text>
            <Input
              value={inviteCode}
              autoFocus
              onChange={(e) => {
                setInviteCode(e.target.value);
              }}
            />
          </label>
          <Button
            type="primary"
            htmlType="submit"
            disabled={!Boolean(inviteCode) || isLoading}
          >
            {isLoading && <Spin />}
            <span>Verify</span>
          </Button>
          {error && (
            <Typography.Text type="danger">{error?.message}</Typography.Text>
          )}
        </form>
        <p className="mt-8">
          PS: If you don't have one, please{" "}
          <a href="https://discord.gg/WJFTPtvGGw ">
            join our Discord community
          </a>{" "}
          to get one!
        </p>
      </Card>
    </Row>
  );
};

/**
 *
 * This component is displayed when the user has already finished the onboarding.
 * flow. It display a message and then redirect to `/home`.
 *
 * @returns
 */
const Onboarded = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate("/home");
  }, []);
  return (
    <div>
      Already onboarded, redirecting to home...
      <DotsFishLoading />
    </div>
  );
};

/**
 *
 * Depending on the user's "onboarding status", ask for invite code, display the onboarding UI, redirect the user to `/home` or related messages.
 *
 * This component re-renders when the user's onboarding status changes, e.g. after a user enters a valid invite code in CheckInviteCode, the status would change to `choose-platform`. This component re-renders and would display ChoosePlatform.
 *
 *
 * @returns
 */
const Onboarding = () => {
  const supabase = getSupaClient();
  const session = supabase.auth.session();
  const user = supabase.auth.user();
  const {
    data: onboardingStatus,
    isLoading,
    error,
  } = useOnboardingStatus(user);
  const navigate = useNavigate();
  useEffect(() => {
    switch (onboardingStatus) {
      case "discord-pick-guilds":
        navigate("/onboarding/discord");
        break;
      case "discord-bot-added":
        navigate("/onboarding/discord/bot-added");
        break;
      case "discord-all-set":
        navigate("/onboarding/discord/all-set");
        break;
      case "slack-await-approval":
        // Corresponding "Slack await approval" message will be shown at home (App.tsx)
        navigate("/home");
        break;
    }
  }, [onboardingStatus]);
  if (session) {
    //TODO probably it's better for the cases below to have their own routes
    switch (onboardingStatus) {
      case "queued":
        return <Queued />;
      case "invited":
        return <CheckInviteCode />;
      case "choose-platform":
        return <ChoosePlatform />;
      case "onboarded":
        return <Onboarded />;
      default:
        return (
          <>
            {isLoading && <Spin />}
            {error && <Typography.Text type="danger">{error}</Typography.Text>}
          </>
        );
    }
  } else {
    return <NotLoggedIn />;
  }
};

export default Onboarding;
